// Canonical player-level model — numeric nivel 0.0–7.0 (NPRP scale, the
// standard pádel rating system) + the six derived display bands. Platform-wide
// single source of truth (decision 2026-07-04): clubmanager CRM and usersmatch
// profiles store the same number and render the same bands. The bands are
// DISPLAY-ONLY — the stored value is always the number.

/**
 * Who set the player's level.
 * - `self`      — player self-rated (usersmatch profile / onboarding)
 * - `club`      — staff estimate (default when set via clubmanager UI)
 * - `playtomic` — reserved for the roadmapped Playtomic level-import
 *                 (docs/product/roadmap.md); no producer yet
 * - `computed`  — derived from match results (future)
 */
export const PLAYER_LEVEL_SOURCES = ['self', 'club', 'playtomic', 'computed'] as const
export type PlayerLevelSource = (typeof PLAYER_LEVEL_SOURCES)[number]

/**
 * A display band derived from the numeric level. Band labels are Spanish;
 * `shortLabel` is for compact UI (e.g. clubmanager's Seg buttons).
 */
export type PlayerLevelBand = {
  key: 'iniciacion' | 'medio_bajo' | 'medio' | 'medio_alto' | 'avanzado' | 'competicion'
  label: string
  shortLabel: string
  min: number
  max: number
  /** Value set when the user picks the band (band chip / Seg button). */
  default: number
  /**
   * What this level looks like ON COURT, in words a player recognises.
   *
   * Six bare chips (`Iniciación · Medio bajo · Medio …`) told a new player
   * nothing: a persona review landed on exactly this — *"anchors the ends of
   * the scale but not the middle; she still cannot tell if she is a 3 or a
   * 4"*. Self-placement is not cosmetic — the feed pre-filters on the band, so
   * a wrong guess quietly hides every match she could actually play.
   *
   * Written around padel's real skill gates rather than years played: using
   * the back wall, and hitting a bandeja. Those are what separate the levels
   * in a way a player can check against their own game.
   */
  description: string
}

export const PLAYER_LEVEL_BANDS: readonly PlayerLevelBand[] = [
  { key: 'iniciacion',  label: 'Iniciación',  shortLabel: 'Inic',  min: 0.0, max: 2.0,  default: 1.0,
    description: 'Empiezas. Te cuesta que la bola pase la red seguido y la pared todavía te gana a ti.' },
  { key: 'medio_bajo',  label: 'Medio bajo',  shortLabel: 'Bajo',  min: 2.0, max: 3.0,  default: 2.5,
    description: 'Mantienes peloteos tranquilos y empiezas a sacar bolas de la pared, aunque no siempre.' },
  { key: 'medio',       label: 'Medio',       shortLabel: 'Medio', min: 3.0, max: 4.0,  default: 3.5,
    description: 'Juegas con la pared sin pensarlo, sacas de bandeja y sabes cuándo subir a la red.' },
  { key: 'medio_alto',  label: 'Medio alto',  shortLabel: 'Alto',  min: 4.0, max: 5.0,  default: 4.5,
    description: 'Colocas la bandeja donde quieres, defiendes por tres metros y decides puntos, no solo los devuelves.' },
  { key: 'avanzado',    label: 'Avanzado',    shortLabel: 'Avanz', min: 5.0, max: 6.0,  default: 5.5,
    description: 'Víbora, remate por tres y salidas de pared con intención. Juegas los puntos con un plan.' },
  { key: 'competicion', label: 'Competición', shortLabel: 'Comp',  min: 6.0, max: 7.01, default: 6.5,
    description: 'Compites en liga o torneos federados y entrenas para ello.' },
] as const

/** Returns the band containing `level`, or null when level is null/undefined. */
export function playerLevelBand(level: number | null | undefined): PlayerLevelBand | null {
  if (level == null) return null
  return PLAYER_LEVEL_BANDS.find((b) => level >= b.min && level < b.max) ?? null
}

/**
 * Format `3.5 · Medio` for display.
 * Returns null when level is null/undefined.
 */
export function formatPlayerLevel(level: number | null | undefined): string | null {
  if (level == null) return null
  const band = playerLevelBand(level)
  return band ? `${level.toFixed(1)} · ${band.label}` : level.toFixed(1)
}

/**
 * A match's level requirement is a range; both null = "Indiferente" (any
 * level, an empty filter — never a stored player value). Band-aligned ranges
 * render as the band name.
 */
export function formatPlayerLevelRange(levelMin: number | null, levelMax: number | null): string {
  if (levelMin == null || levelMax == null) return 'Nivel indiferente'
  const band = PLAYER_LEVEL_BANDS.find((b) => b.min === levelMin && b.max === levelMax)
  return band ? `Nivel ${band.label}` : `Nivel ${levelMin.toFixed(1)}–${Math.min(levelMax, 7).toFixed(1)}`
}
